import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { BuilderProvider } from './contexts/BuilderContext.tsx';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { initTestimonialCarousel } from './lib/carousel.ts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { registerToast } from '@/lib/utils';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // Default: data is fresh for 2 minutes
      gcTime: 5 * 60 * 1000, // Default: keep unused data in cache for 5 minutes
      retry: (failureCount, error) => {
        const status =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { status?: number } }).response?.status;

        // Don't retry auth failures or rate limits — retrying amplifies request storms.
        if (status === 401 || status === 403 || status === 429) {
          return false;
        }

        return failureCount < 2;
      },
    },
  },
});

// If we globally log out on 401, also clear any cached server state.
window.addEventListener('auth:unauthorized', () => {
  queryClient.cancelQueries();
  queryClient.clear();
});

// Drop stale entity list caches from prior sessions/projects after a fresh login.
window.addEventListener('auth:login-success', () => {
  queryClient.removeQueries({
    predicate: (query) => {
      const rootKey = query.queryKey[0];
      return rootKey === 'components' || rootKey === 'modules' || rootKey === 'projects';
    },
  });
});

registerToast(({ title, description, variant }) => {
  toast({
    title,
    description,
    variant: variant === 'destructive' ? 'destructive' : 'default',
  });
});

createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <BuilderProvider>
        <App />
      </BuilderProvider>
    </AuthProvider>
  </QueryClientProvider>
);

// Initialize testimonial carousel
initTestimonialCarousel();
